import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Row } from "antd";
import csvDownload from "json-to-csv-export";
import { SpendCategory, VendorCountResponse, VendorOrderForm } from "../../../types/VendorTypes";
import { PageRequest, PagerObject } from "../../../types/DataFetchingTypes";
import {
    getVendorCategories,
    getVendorCount,
    getVendorOrderForms,
} from "../../../common/api/VendorClient";
import { Metrics } from "./components/Metrics";
import { Vendors, VendorSorter, addSortKeys } from "./components/Vendors";
import { handleError } from "../../../common/ErrorHandling";
import { formatDateString } from "../../date/DateOp";
import { formatNumber, roundToTwoDecimals } from "../../../common/Number";
import { useSearchParams } from "react-router-dom";

const VENDOR_CATEGORY_FILTER_KEY = "vendorCategoryFilter";

// Helper to compute SHA-256 hash of the categories hierarchy (first 8 bytes, in hex)
const getCategoryHash = async (categories: SpendCategory[]): Promise<string> => {
    const structure = categories.map((cat) => ({
        label: cat.label,
        children: cat.children?.map((child) => ({ label: child.label })) || [],
    }));
    const json = JSON.stringify(structure);

    // Use SubtleCrypto for standard SHA-256
    const encoder = new TextEncoder();
    const data = encoder.encode(json);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = new Uint8Array(hashBuffer);

    // Return first 8 bytes as hex string (16 characters)
    return Array.from(hashArray.slice(0, 8))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
};

// Build category index lookup maps in a single pass
const buildCategoryIndexLookups = (categories: SpendCategory[]) => {
    const labelToIndex = new Map<string, string>();
    const categoryDescendants = new Map<string, string[]>();

    for (let catIdx = 0; catIdx < categories.length; catIdx++) {
        const category = categories[catIdx];
        const catIndexStr = String(catIdx + 1); // 1-based

        if (category.children) {
            const childLabels: string[] = [];
            for (let subIdx = 0; subIdx < category.children.length; subIdx++) {
                const subCategory = category.children[subIdx];
                const subLabel = subCategory.label;
                const subCatIndexStr = `${catIndexStr}.${subIdx + 1}`;

                childLabels.push(subLabel);
                labelToIndex.set(subLabel, subCatIndexStr);
                categoryDescendants.set(subCatIndexStr, [subLabel]);
            }
            categoryDescendants.set(catIndexStr, childLabels);
        }
    }

    return { labelToIndex, categoryDescendants };
};

// Helper to check if all descendants are selected
const areAllDescendantsSelected = (descendants: string[], filter: string[]): boolean => {
    if (!descendants?.length) return false;
    return descendants.every((label) => filter.includes(label));
};

// Helper to convert filter (category labels) to hierarchical indices (1-based)
const filterToIndices = (filter: string[], categoryState: CategoryState): string[] => {
    const indices: string[] = [];
    const processedCategories = new Set<string>();

    // First pass: check for whole categories (top-level only, not subcategories)
    for (const [indexStr, descendants] of Array.from(categoryState.categoryDescendants.entries())) {
        // Only process top-level categories (no dot in index)
        if (!indexStr.includes(".") && areAllDescendantsSelected(descendants, filter)) {
            indices.push(indexStr);
            processedCategories.add(indexStr);
        }
    }

    // Second pass: add individual subcategories not part of whole categories
    for (const filterValue of filter) {
        const indexStr = categoryState.labelToIndex.get(filterValue);
        if (indexStr) {
            // Extract parent category index from "1.1" format
            const catIndexStr = indexStr.split(".")[0];
            if (!processedCategories.has(catIndexStr)) {
                indices.push(indexStr);
            }
        }
    }

    return indices;
};

// Helper to convert hierarchical indices back to filter (category labels, 1-based)
const indicesToFilter = (indices: string[], categoryState: CategoryState): string[] => {
    const filter: string[] = [];

    for (const index of indices) {
        // Look up descendants for this index (works for both categories and subcategories)
        const descendants = categoryState.categoryDescendants.get(index);
        if (descendants) {
            filter.push(...descendants);
        }
    }

    return filter;
};

// Helper to encode filter as hash + indices for URL parameter (Base64 encoded)
export const encodeFilterForUrl = (filter: string[], categoryState: CategoryState): string => {
    const indices = filterToIndices(filter, categoryState);
    // Format: hash:index1,index2,index3 then Base64 encode
    const payload = `${categoryState.hash}:${indices.join(",")}`;
    return btoa(payload).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, ""); // base64url
};

// Helper to decode filter from hash + indices URL parameter (Base64 encoded)
export const decodeFilterFromUrl = (
    encoded: string,
    categoryState: CategoryState
): string[] | null => {
    try {
        // Decode base64url
        const base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
        const payload = atob(base64);
        const [urlHash, indicesStr] = payload.split(":");

        // Check if hash matches
        if (urlHash !== categoryState.hash) {
            console.warn("Category hierarchy mismatch - URL filter may not work correctly");
            // Proceed with best effort
        }

        const indices = indicesStr.split(",").filter(Boolean);
        return indicesToFilter(indices, categoryState);
    } catch {
        return null;
    }
};

type CategoryFilterState = {
    values: string[];
    shouldPersist: boolean;
};

export type CategoryState = {
    categories: SpendCategory[];
    labelToIndex: Map<string, string>;
    categoryDescendants: Map<string, string[]>;
    hash: string;
};

export const buildCategoryState = async (categories: SpendCategory[]): Promise<CategoryState> => {
    const { labelToIndex, categoryDescendants } = buildCategoryIndexLookups(categories);
    const hash = await getCategoryHash(categories);
    return {
        categories,
        labelToIndex,
        categoryDescendants,
        hash,
    };
};

export const VendorsView = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [pagerData, setPagerData] = useState<PagerObject<VendorOrderForm[]>>();
    const [count, setCount] = useState<VendorCountResponse>();
    const [categoryState, setCategoryState] = useState<CategoryState>();
    const [categoryFilterState, setCategoryFilterState] = useState<CategoryFilterState>({
        values: [],
        shouldPersist: false,
    });
    const [vendorNameFilter, setVendorNameFilter] = useState<string>("");
    const [sorter, setSorter] = useState<VendorSorter | undefined>(undefined);

    // Track abort controllers to cancel pending requests
    const vendorOrderFormsAbortController = useRef<AbortController | null>(null);
    const vendorCountAbortController = useRef<AbortController | null>(null);

    const loadVendorOrderForms = async (pager?: PageRequest) => {
        // Cancel any pending request
        if (vendorOrderFormsAbortController.current) {
            vendorOrderFormsAbortController.current.abort();
        }

        // Create new abort controller for this request
        const ac = new AbortController();
        vendorOrderFormsAbortController.current = ac;

        setLoading(true);
        try {
            const response = await getVendorOrderForms(
                undefined,
                categoryFilterState.values,
                vendorNameFilter,
                { signal: ac.signal }
            );
            setPagerData(response);
        } catch (e) {
            // Ignore abort errors
            if (!ac.signal.aborted) {
                handleError(e);
            }
        } finally {
            if (!ac.signal.aborted) {
                setLoading(false);
            }
        }
    };

    const loadVendorCount = async () => {
        // Cancel any pending request
        if (vendorCountAbortController.current) {
            vendorCountAbortController.current.abort();
        }

        // Create new abort controller for this request
        const ac = new AbortController();
        vendorCountAbortController.current = ac;

        try {
            const response = await getVendorCount(categoryFilterState.values, vendorNameFilter, {
                signal: ac.signal,
            });
            setCount(response);
        } catch (e) {
            // Ignore abort errors
            if (!ac.signal.aborted) {
                handleError(e);
            }
        }
    };

    const loadCategories = async () => {
        try {
            const response = await getVendorCategories();
            const categoryState = await buildCategoryState(response);
            setCategoryState(categoryState);
        } catch (e) {
            handleError(e);
        }
    };

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
        // Convert SorterResult to VendorSorter
        if (sorter.columnKey && sorter.order) {
            setSorter({
                columnKey: String(sorter.columnKey),
                order: sorter.order,
            });
        } else {
            setSorter(undefined);
        }

        // Handle category filter changes (only update if actually changed)
        const newCategoryFilter = filters.spendCategory || [];
        if (JSON.stringify(newCategoryFilter) !== JSON.stringify(categoryFilterState.values)) {
            setCategoryFilterState({ values: newCategoryFilter, shouldPersist: true });
        }

        // Handle vendor name filter changes (only update if actually changed)
        const newVendorName = filters.name && filters.name.length > 0 ? filters.name[0] : "";
        if (newVendorName !== vendorNameFilter) {
            setVendorNameFilter(newVendorName);
        }
    };

    const handleDownload = async () => {
        const metricHeaders = [
            "Number of vendors",
            "Number of contracts",
            "Total spend",
            "Total est. savings",
            "",
            "",
        ];

        const metricData = [
            {
                col1: pagerData?.pagination.total,
                col2: count?.contracts,
                col3: formatNumber(count?.spend || 0),
                col4: formatNumber(count?.savings || 0),
                col5: "",
                col6: "",
            },
        ];

        const dividerData = [
            {
                col1: "",
                col2: "",
                col3: "",
                col4: "",
                col5: "",
                col6: "",
            },
            {
                col1: "Vendor",
                col2: "Contracts",
                col3: "Average score",
                col4: "Total value",
                col5: "Est. savings",
                col6: "Spend category",
            },
        ];

        const vendorData =
            pagerData?.data.map((item) => ({
                col1: item.name,
                col2: item.metrics.orders,
                col3: `${roundToTwoDecimals(item.metrics.score || 0)}%`,
                col4: formatNumber(item.metrics.value || 0),
                col5: formatNumber(item.metrics.savings || 0),
                col6: item.subSpendCategory || "",
            })) || [];

        csvDownload({
            headers: metricHeaders,
            data: [...metricData, ...dividerData, ...vendorData],
            filename: `Vendors_${formatDateString(new Date())}`,
        });
    };

    useEffect(() => {
        loadCategories();

        // Initialize vendor name filter from URL
        const urlVendorName = searchParams.get("q");
        if (urlVendorName) {
            setVendorNameFilter(urlVendorName);
        }

        // Initialize sort from URL
        const urlSortKey = searchParams.get("sort");
        if (urlSortKey) {
            // Determine sort direction based on column (matches sortDirections in Vendors.tsx)
            // "name" and "spendCategory" sort ascending, all metrics sort descending
            const sortDirection =
                urlSortKey === "name" || urlSortKey === "spendCategory" ? "ascend" : "descend";
            setSorter({
                columnKey: urlSortKey,
                order: sortDirection,
            });
        }
    }, []);

    // Initialize filter from URL or localStorage on mount (after categories are loaded)
    useEffect(() => {
        if (!categoryState) return; // Wait for category state to load

        const urlFilterParam = searchParams.get("f");

        if (urlFilterParam) {
            // URL has priority - decode and expand it
            const decodedFilter = decodeFilterFromUrl(urlFilterParam, categoryState);
            if (decodedFilter) {
                setCategoryFilterState({ values: decodedFilter, shouldPersist: false });
            }
            return;
        }

        // No URL param - try localStorage
        try {
            const saved = localStorage.getItem(VENDOR_CATEGORY_FILTER_KEY);
            if (saved) {
                const parsedFilter = JSON.parse(saved);
                setCategoryFilterState({ values: parsedFilter, shouldPersist: false });
            }
        } catch {
            // Ignore localStorage errors
        }
    }, [categoryState]); // Only run when category state is loaded

    // Sync filter to URL and localStorage
    useEffect(() => {
        if (!categoryState) return; // Wait for category state to load

        if (categoryFilterState.values.length > 0) {
            // Encode for URL (uses cached state with hash and lookups)
            const encoded = encodeFilterForUrl(categoryFilterState.values, categoryState);
            searchParams.set("f", encoded);
            setSearchParams(searchParams, { replace: true });

            // Only save to localStorage if this was a user interaction
            if (categoryFilterState.shouldPersist) {
                localStorage.setItem(
                    VENDOR_CATEGORY_FILTER_KEY,
                    JSON.stringify(categoryFilterState.values)
                );
            }
        } else {
            // Clear URL param
            searchParams.delete("f");
            setSearchParams(searchParams, { replace: true });

            // Only save to localStorage if this was a user interaction
            if (categoryFilterState.shouldPersist) {
                localStorage.removeItem(VENDOR_CATEGORY_FILTER_KEY);
            }
        }
    }, [categoryFilterState, categoryState]);

    // Sync vendor name filter to URL (only if different from current URL)
    useEffect(() => {
        const currentQ = searchParams.get("q");
        const newQ = vendorNameFilter || null;

        if (currentQ !== newQ) {
            if (newQ) {
                searchParams.set("q", newQ);
            } else {
                searchParams.delete("q");
            }
            setSearchParams(searchParams, { replace: true });
        }
    }, [vendorNameFilter]);

    // Sync sort to URL (only if different from current URL)
    useEffect(() => {
        const currentSort = searchParams.get("sort");
        const newSort = sorter?.columnKey ? String(sorter.columnKey) : null;

        if (currentSort !== newSort) {
            if (newSort) {
                searchParams.set("sort", newSort);
            } else {
                searchParams.delete("sort");
            }
            setSearchParams(searchParams, { replace: true });
        }
    }, [sorter]);

    useEffect(() => {
        loadVendorOrderForms();
        loadVendorCount();
    }, [categoryFilterState, vendorNameFilter]);

    // Recompute sort keys when vendor data or categories load
    const vendorsWithSortKeys = useMemo(() => {
        return pagerData && addSortKeys(pagerData.data, categoryState?.categories);
    }, [pagerData, categoryState]);

    return (
        <Card bordered={false}>
            <div
                style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    background: "#fff",
                    margin: -24,
                    padding: 24,
                }}
            >
                <Metrics
                    vendors={pagerData?.pagination.total}
                    spend={count?.spend || 0}
                    {...count}
                />
                <Row style={{ marginTop: 20 }}>
                    <Col flex={1}>
                        <h3 style={{ margin: 0 }}>Vendors</h3>
                        <p style={{ margin: 0 }}>View and filter your vendors by category.</p>
                    </Col>
                    <Col>
                        <Button type="primary" onClick={handleDownload}>
                            Download CSV
                        </Button>
                    </Col>
                </Row>
            </div>
            <Row gutter={20} style={{ marginTop: 20 }}>
                <Col span={24}>
                    <Vendors
                        loading={loading}
                        dataSource={vendorsWithSortKeys}
                        pagination={false}
                        onChange={handleTableChange}
                        sticky={{ offsetHeader: 159 + 24 + 20 + 48 + 20 }}
                        categories={categoryState?.categories}
                        categoryFilter={categoryFilterState.values}
                        vendorNameFilter={vendorNameFilter}
                        currentSorter={sorter}
                    />
                </Col>
            </Row>
        </Card>
    );
};
